<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
  column: { type: Object, required: true },
  anchor: { type: Object, default: null },
});
const emit = defineEmits(['save', 'cancel']);

const draft = ref(props.column.description || '');
const isSaving = ref(false);
const popoverRef = ref(null);
const textareaRef = ref(null);
const style = ref({});

function computeStyle() {
  const a = props.anchor;
  if (!a) {
    style.value = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
    };
    return;
  }
  const width = 300;
  let left = a.left;
  if (left + width > window.innerWidth - 8)
    left = window.innerWidth - width - 8;
  left = Math.max(8, left);
  style.value = {
    top: `${a.bottom + 6}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
}

async function save() {
  isSaving.value = true;
  try {
    await Promise.resolve(emit('save', draft.value.trim()));
  } finally {
    isSaving.value = false;
  }
}

function onDocMousedown(e) {
  if (popoverRef.value && !popoverRef.value.contains(e.target)) emit('cancel');
}

function onKeydown(e) {
  if (e.key === 'Escape') emit('cancel');
}

onMounted(() => {
  nextTick(() => {
    computeStyle();
    textareaRef.value?.focus();
  });
  window.addEventListener('mousedown', onDocMousedown);
  window.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  window.removeEventListener('mousedown', onDocMousedown);
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      ref="popoverRef"
      class="fixed z-[10010] bg-n-background border border-n-weak rounded-xl shadow-xl p-3 flex flex-col gap-2"
      :style="style"
    >
      <p class="text-xs font-medium text-n-slate-11">
        {{ $t('KANBAN.COLUMN.DESCRIPTION_LABEL') }}
      </p>
      <textarea
        ref="textareaRef"
        v-model="draft"
        rows="4"
        :placeholder="$t('KANBAN.COLUMN.DESCRIPTION_PLACEHOLDER')"
        class="w-full resize-none rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
      />
      <div class="flex items-center justify-end gap-2">
        <button
          class="rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-1"
          @click="emit('cancel')"
        >
          {{ $t('KANBAN.COLUMN.CANCEL') }}
        </button>
        <button
          class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:brightness-110 disabled:opacity-50"
          :disabled="isSaving"
          @click="save"
        >
          {{ $t('KANBAN.COLUMN.SAVE') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>
